// SaldoCreditos.js — os dois saldos: no cabeçalho do perfil e no do site.
//
// ================= POR QUE SAIU DE UMA ABA E VEIO PARA CÁ =================
//
// As Insigne Coins tinham aba própria, e uma aba é um lugar onde se ENTRA. Saldo
// não é assunto: é estado, e estado se lê de relance. Quem abria o perfil para
// ver outra coisa não descobria que tinha 340 coins guardadas — e moeda que a
// pessoa não sabe que tem não compra nada.
//
// Em 11/09/2026 ele ganhou o segundo lugar, a pedido do dono: o cabeçalho do
// SITE, à esquerda da foto de perfil, com os dois valores empilhados e o "+" ao
// lado. Ali o saldo aparece em toda página — inclusive na do livro, que é onde
// a coin vira desconto.
//
// AS CORES SÃO A LEITURA. Ticket em vermelho, coin em laranja: dois números a
// dois dedos de distância precisam se distinguir antes de qualquer rótulo.
//
// ================= UMA REQUISIÇÃO PARA OS DOIS LUGARES =================
//
// Com o componente montado no cabeçalho do site E no do perfil, a resposta é
// guardada num cache de MÓDULO por 60 segundos: abrir o perfil não repete a
// consulta que o cabeçalho acabou de fazer, e navegar entre páginas não refaz
// nada. Não é preciosismo — o banco desta hospedagem tem teto de conexões por
// hora, e este é o único pedaço do site que toca o servidor em TODA página de
// quem está logado.
//
// O evento `creditosAtualizados` fura o cache: quando o saldo muda de verdade
// (troca de coins por ticket, uso no Clube), os dois cabeçalhos atualizam na
// hora.
import React, { useCallback, useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API;
const tokenKey = "token" + (process.env.REACT_APP_USER_FUNCTION || "");

// Em tela de toque não existe passar o mouse: ali o "+" fica sempre visível,
// senão ele sumiria para sempre. A pergunta é feita uma vez, no carregamento.
const SEM_HOVER =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(hover: none)").matches
    : false;

const VALIDADE_MS = 60 * 1000;
let cache = { dados: null, em: 0 };
let emVoo = null;

/** Busca o saldo, reaproveitando o que foi buscado há menos de um minuto. */
const buscarSaldo = (forcar = false) => {
  const token = localStorage.getItem(tokenKey);
  if (!token) return Promise.resolve(null);

  const agora = Date.now();
  if (!forcar && cache.dados && agora - cache.em < VALIDADE_MS) {
    return Promise.resolve(cache.dados);
  }
  // Duas montagens no mesmo instante (cabeçalho do site e do perfil) dividem a
  // MESMA promessa: sem isto seriam duas consultas para a mesma resposta.
  if (!forcar && emVoo) return emVoo;

  emVoo = fetch(API_URL + "creditos/saldo", { headers: { Authorization: "Bearer " + token } })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d) cache = { dados: d, em: Date.now() };
      return d;
    })
    .catch(() => null)
    .finally(() => { emVoo = null; });

  return emVoo;
};

/** O saldo some junto com a sessão: quem sai não pode ver o número de quem entrou. */
export const esquecerSaldo = () => { cache = { dados: null, em: 0 }; };

// `onCarregar` devolve a resposta inteira para quem desenha o cabeçalho do
// perfil: a MESMA requisição traz o estado do Clube, e o selo "Clube Insigne"
// mora ao lado do selo de autor, fora deste componente.
//
// `layout="coluna"` é a versão do cabeçalho do site: os dois valores empilhados
// e o "+" à direita deles, como o dono desenhou.
const SaldoCreditos = ({ onComprar, onCarregar, className = "", layout = "linha" }) => {
  const [saldo, setSaldo] = useState(cache.dados);
  // O "+" do cabeçalho do site aparece com o cursor (ou o foco) sobre o saldo.
  const [sobre, setSobre] = useState(false);

  const carregar = useCallback(async (forcar) => {
    const d = await buscarSaldo(forcar);
    if (!d) return;
    setSaldo(d);
    if (onCarregar) onCarregar(d);
    // `onCarregar` fora das dependências de propósito: o Perfil passa uma
    // função nova a cada render, e com ela aqui este efeito refaria a
    // requisição a cada tecla digitada em qualquer lugar do perfil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    carregar(false);
    // A loja de créditos avisa quando o saldo muda (a troca de coins por ticket
    // acontece sem recarregar a página). Aí o cache é furado de propósito.
    const aoAtualizar = () => carregar(true);
    window.addEventListener("creditosAtualizados", aoAtualizar);
    return () => window.removeEventListener("creditosAtualizados", aoAtualizar);
  }, [carregar]);

  // Enquanto não carregou, não desenha nada: um "0" que vira "340" um segundo
  // depois é pior do que o espaço vazio — parece que o saldo sumiu e voltou.
  if (!saldo) return null;

  const coluna = layout === "coluna";

  const Valor = ({ icone, valor, cor, titulo, rotulo }) => (
    // A SUPERFÍCIE MUDA CONFORME O FUNDO (pedido do dono, 11/09/2026).
    //
    // No perfil, a pastilha fica sobre o fundo da PÁGINA e `insigne-navy-soft`
    // já é mais clara que ele. No cabeçalho do site o fundo é o navy da barra,
    // quase da mesma cor — e ali a pastilha sumia, parecendo um número solto.
    // Branco translúcido resolve os dois casos sem um segundo token de cor:
    // ele clareia o que estiver embaixo, seja qual for.
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-controle border " +
        (coluna
          ? "border-white/20 bg-white/10 px-2 py-0.5 text-xs leading-5 "
          : "border-line bg-insigne-navy-soft px-2.5 py-1.5 text-sm ") +
        "font-bold " + cor
      }
      title={titulo}
    >
      <i className={"bi " + icone} aria-hidden="true" />
      {valor}
      <span className="sr-only">{rotulo}</span>
    </span>
  );

  return (
    // ===== POR QUE O SUMIÇO DO "+" É ESTADO, E NÃO `group-hover` =====
    //
    // A primeira versão usava `opacity-0 group-hover:opacity-100`, e o botão
    // ficava invisível PARA SEMPRE. O motivo é que o site carrega o Bootstrap
    // junto do Tailwind, e o Bootstrap declara `.opacity-0{opacity:0!important}`
    // — o `!important` vence qualquer variante do Tailwind, que não usa
    // `!important`. Qualquer classe chamada `opacity-0` neste projeto é uma
    // armadilha (o card de livro já resolvia isso com `style` inline).
    //
    // Com estado + `style`, nenhuma classe entra na disputa.
    <div
      className={"flex items-center gap-1.5 " + className}
      onMouseEnter={coluna ? () => setSobre(true) : undefined}
      onMouseLeave={coluna ? () => setSobre(false) : undefined}
      onFocus={coluna ? () => setSobre(true) : undefined}
      onBlur={coluna ? () => setSobre(false) : undefined}
    >
      <div className={coluna ? "flex flex-col gap-1" : "flex items-center gap-1.5"}>
        <Valor
          icone="bi-ticket-perforated-fill"
          valor={saldo.tickets}
          cor="text-insigne-ticket"
          titulo={`${saldo.tickets} Ticket(s) do Clube`}
          rotulo="Tickets"
        />
        <Valor
          icone="bi-coin"
          valor={saldo.coins}
          cor="text-insigne-orange"
          titulo={`${saldo.coins} Insigne Coins`}
          rotulo="Insigne Coins"
        />
      </div>

      <button
        type="button"
        onClick={onComprar}
        aria-label="Comprar Tickets e Coins"
        title="Comprar mais"
        className={
          "inline-flex items-center justify-center rounded-controle text-insigne-orange " +
          "transition-[background-color,opacity] duration-200 " +
          (coluna
            ? "h-8 w-8 border border-insigne-orange/60 bg-insigne-orange/15 hover:bg-insigne-orange/25"
            : "h-[34px] w-[34px] border border-insigne-orange/40 hover:bg-insigne-orange/10")
        }
        // ===== O "+" SÓ APARECE COM O CURSOR NO SALDO =====
        // (pedido do dono, 11/09/2026) Ele é a ação, não o número: aceso o
        // tempo todo no cabeçalho de toda página, virava um terceiro elemento
        // disputando atenção com Tickets e Coins.
        //
        // Some por TRANSPARÊNCIA, e não por sumir do layout: o espaço fica
        // reservado, e nada se mexe quando ele volta.
        style={
          coluna && !SEM_HOVER
            ? { opacity: sobre ? 1 : 0, pointerEvents: sobre ? "auto" : "none" }
            : undefined
        }
      >
        <i className="bi bi-plus-lg text-sm" aria-hidden="true" />
      </button>
    </div>
  );
};

export default SaldoCreditos;
